// The fake api's rate card (F-PRC-10 … F-PRC-12; spec/06 §4), in the contract's shapes — enough for the Pricing tab's
// rate card to be rendered and published against `fake-server.ts`:
//
//   GET  /v1/organizations/{o}/venues/{v}/rate-card   getVenueRateCard      pricing:read
//   POST /v1/organizations/{o}/venues/{v}/rate-card   publishVenueRateCard  pricing:manage
//
// Starts with no card (every class pays the flat ₹60 an hour). A publish is checked with the contract's own schema and
// becomes the version in force (or a scheduled one, dated later). What a class pays "now" is its base rate: the fake
// resolves no time-of-day band — the server does, with `@clavian/domain` `resolveCardRate`.
import type { Problem, RateCardVersion, RateCardView } from '@clavian/api-client';
import { PublishRateCardSchema } from '@clavian/contracts';

import type { FakeResponse } from './fake-server';

const ROUTE = /^\/v1\/organizations\/([^/]+)\/venues\/([^/]+)\/rate-card$/;
const NOW = '2026-09-22T09:00:00.000Z';

export interface RateCardHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  readonly can: (permission: string) => boolean;
  /** The venue's flat pay-as-you-go rate, from the fake's settings. */
  readonly flatRate: () => number;
}

export function createFakeRateCard(helpers: RateCardHelpers) {
  const { json, problem } = helpers;
  const state = { versions: [] as RateCardVersion[], publishes: [] as unknown[] };

  function view(venueId: string): RateCardView {
    const started = state.versions.filter((version) => version.effectiveFrom <= NOW);
    const current = started[started.length - 1] ?? null;
    const status = (version: RateCardVersion): RateCardVersion => ({
      ...version,
      status: version.effectiveFrom > NOW ? 'scheduled' : version.id === current?.id ? 'in_force' : 'superseded',
    });
    return {
      venueId,
      currency: 'INR',
      timezone: 'Asia/Kolkata',
      payAsYouGoEnabled: true,
      fallback: { hourlyAmount: { amount: helpers.flatRate(), currency: 'INR' }, billingUnitSeconds: 60 },
      scheduled: state.versions.filter((version) => version.effectiveFrom > NOW).map(status),
      history: [...state.versions].reverse().map(status),
      current: current === null ? null : status(current),
      now: (current?.rates ?? []).map((rate) => ({ stationClassId: rate.stationClassId, hourlyAmount: rate.hourlyAmount, band: null, nextChange: null })),
      serverTime: NOW,
    } as RateCardView;
  }

  function handle(method: string, pathname: string, input: Record<string, unknown>): FakeResponse | undefined {
    const match = ROUTE.exec(pathname);
    if (match === null) return undefined;
    const venueId = match[2]!;
    if (method === 'GET') return helpers.can('pricing:read') ? json(200, view(venueId)) : problem(403, 'forbidden', 'Not allowed');
    if (method !== 'POST') return undefined;
    if (!helpers.can('pricing:manage')) return problem(403, 'forbidden', 'Not allowed');
    const parsed = PublishRateCardSchema.safeParse(input);
    if (!parsed.success) return problem(400, 'validation_failed', 'The request was not valid.', { detail: parsed.error.issues.map((issue) => issue.message).join('; ') });
    state.publishes.push(input);
    const version = state.versions.length + 1;
    state.versions.push({
      id: `rcv_01k5znw4hmfay8vvr14d2pf${String(version).padStart(3, '0')}`,
      venueId,
      version,
      name: parsed.data.name,
      effectiveFrom: parsed.data.effectiveFrom ?? NOW,
      publishedAt: NOW,
      status: 'in_force',
      rates: parsed.data.rates,
    } as RateCardVersion);
    return json(201, view(venueId));
  }

  return { state, handle };
}
